/**
 * Nav availability — maps (interest, country) pairs to page URLs
 * and provides bidirectional filtering for the MadLibs nav.
 */

import { getSlugForCountry } from './slug-country';
import { getCountrySlugFromCode, COUNTRY_NAME_MAP } from './long-weekends';
import { getCities } from './data-source';
import { getCitySlugForId } from './slug-city';
import { getEvergreenSlugEntry } from './slug-evergreen';
import type { CountryCode } from './types';

// ---------------------------------------------------------------------------
// Interest type
// ---------------------------------------------------------------------------

export type InterestType = 'holidays' | 'festivals' | 'longweekends' | 'besttime' | 'localevents';

// ---------------------------------------------------------------------------
// Static availability maps (derived from page registries)
// ---------------------------------------------------------------------------

/** Countries that have festival index pages */
const FESTIVAL_COUNTRIES = new Set<CountryCode>(['TH', 'VN', 'ID', 'IN', 'MY', 'SG', 'PH']);

/** Countries that have best-time-to-visit pages */
const BEST_TIME_COUNTRIES = new Set<CountryCode>([
  'TH', 'ID', 'VN', 'MN', 'ET', 'IN', 'MY', 'PH', 'SG', 'EG', 'SA', 'TR', 'LA', 'MM',
]);

/** Countries that have long-weekend pages (from COUNTRY_NAME_MAP) */
const LONG_WEEKEND_COUNTRIES = new Set<CountryCode>(
  Object.values(COUNTRY_NAME_MAP) as CountryCode[]
);

/** Slug prefix used for best-time pages (country code → slug fragment) */
const BEST_TIME_SLUG_MAP: Record<string, string> = {
  TH: 'thailand', ID: 'indonesia', VN: 'vietnam', MN: 'mongolia',
  ET: 'ethiopia', IN: 'india', MY: 'malaysia', PH: 'philippines',
  SG: 'singapore', EG: 'egypt', SA: 'saudi-arabia', TR: 'turkey',
  LA: 'laos', MM: 'myanmar',
};

/** Festival slug base (country code → base slug without "-festivals") */
const FESTIVAL_SLUG_MAP: Record<string, string> = {
  TH: 'thailand', VN: 'vietnam', ID: 'indonesia', IN: 'india',
  MY: 'malaysia', SG: 'singapore', PH: 'philippines',
};


// ---------------------------------------------------------------------------
// Availability queries
// ---------------------------------------------------------------------------

/** Which interests have pages for the given country? */
export function getAvailableInterests(country: CountryCode): Set<InterestType> {
  const set = new Set<InterestType>();
  // Only add holidays if the country actually has a generated slug
  const slug = getSlugForCountry(country, 'en');
  if (slug) set.add('holidays');

  if (FESTIVAL_COUNTRIES.has(country)) set.add('festivals');
  if (LONG_WEEKEND_COUNTRIES.has(country)) set.add('longweekends');
  if (BEST_TIME_COUNTRIES.has(country)) set.add('besttime');

  // Check if any city with a generated page exists for this country
  const cities = getCities();
  if (cities.some(c => c.country === country && getCitySlugForId(c.id, 'en'))) set.add('localevents');

  return set;
}

/** Which countries have pages for the given interest? Returns empty set for 'holidays' meaning "all". */
export function getCountriesForInterest(interest: InterestType): Set<CountryCode> {
  switch (interest) {
    case 'holidays':
      return new Set(); // empty means "all countries" — caller shows full list
    case 'festivals':
      return FESTIVAL_COUNTRIES;
    case 'longweekends':
      return LONG_WEEKEND_COUNTRIES;
    case 'besttime':
      return BEST_TIME_COUNTRIES;
    case 'localevents': {
      const cities = getCities();
      return new Set(cities.map(c => c.country));
    }
  }
}

/** Resolve (interest, country, lang) → target URL path (without leading /). Returns null if no page exists. */
export function getTargetUrl(interest: InterestType, country: CountryCode, lang: string): string | null {
  switch (interest) {
    case 'holidays': {
      const slug = getSlugForCountry(country, lang) || getSlugForCountry(country, 'en');
      return slug || null;
    }
    case 'festivals': {
      const base = FESTIVAL_SLUG_MAP[country];
      if (!base) return null;
      return `${base}-festivals`;
    }
    case 'longweekends': {
      const slug = getCountrySlugFromCode(country);
      return slug || null;
    }
    case 'besttime': {
      const base = BEST_TIME_SLUG_MAP[country];
      if (!base) return null;
      return `best-time-to-visit-${base}`;
    }
    case 'localevents': {
      const cities = getCities();
      const city = cities.find(c => c.country === country);
      if (!city) return null;
      const slug = getCitySlugForId(city.id, lang) || getCitySlugForId(city.id, 'en');
      return slug || null;
    }
  }
}

// ---------------------------------------------------------------------------
// URL inference — detect current page's interest + country from pathname
// ---------------------------------------------------------------------------

export function inferNavStateFromPath(pathname: string): { interest?: InterestType; country?: CountryCode } {
  const path = pathname.replace(/^\//, '');
  if (!path) return {};

  // Best time to visit
  const bestTimeMatch = path.match(/^best-time-to-visit-(.+)$/);
  if (bestTimeMatch) {
    const frag = bestTimeMatch[1];
    for (const [code, slug] of Object.entries(BEST_TIME_SLUG_MAP)) {
      if (slug === frag) return { interest: 'besttime', country: code as CountryCode };
    }
  }

  // Festivals
  const festivalMatch = path.match(/^(.+)-festivals$/);
  if (festivalMatch) {
    const frag = festivalMatch[1];
    for (const [code, slug] of Object.entries(FESTIVAL_SLUG_MAP)) {
      if (slug === frag) return { interest: 'festivals', country: code as CountryCode };
    }
  }

  // Long weekends
  const lwMatch = path.match(/^(.+)-long-weekends-\d{4}$/);
  if (lwMatch) {
    const frag = lwMatch[1];
    const code = COUNTRY_NAME_MAP[frag];
    if (code) return { interest: 'longweekends', country: code };
  }

  // Fallback: look up slug in the evergreen registry (covers cross-calendar,
  // country-month, and any future page types without hardcoding)
  const evg = getEvergreenSlugEntry(path);
  if (evg) {
    const code = (evg.data.countryCode ?? evg.data.destCountryCode) as CountryCode | undefined;
    if (code) {
      const interestMap: Record<string, InterestType> = {
        'cross-calendar': 'holidays',
        'country-month': 'besttime',
        'best-time': 'besttime',
        'festival-index': 'festivals',
        'long-weekend': 'longweekends',
      };
      const interest = interestMap[evg.pageType];
      if (interest) return { interest, country: code };
      // Unknown page type but has a country — default to holidays
      return { interest: 'holidays', country: code };
    }
  }

  return {};
}
